/**
 * EditCoursePage
 *
 * Full-page editor for creating or editing a Course / Learning Path.
 *
 * Routes:
 *   /courses/new                  → create mode (?type=path for Learning Path)
 *   /courses/:slug/edit           → edit existing course by slug
 *
 * Layout:
 *   - Rich header: title + status pill + type pill + Preview / More actions
 *   - Tab strip: Settings / Detail Page / Curriculum / Completion
 *   - Two-column body on edit (form + metadata sidebar); single-column on
 *     create (no metadata yet to surface)
 */

import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Course, CourseType } from '../apis/api-client';
import { isContentAuthorRole } from '../utils/roleHelpers';
import { coursesClient } from '../apis/client-instances';
import EditCourseForm from '../components/courses/EditCourseForm';
import CourseDetailPageTab from '../components/courses/CourseDetailPageTab';
import CourseCurriculumTab from '../components/courses/CourseCurriculumTab';
import CourseCompletionTab from '../components/courses/CourseCompletionTab';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '../components/ui';
import {
  ContentCopyIcon,
  VisibilityIcon,
} from '../components/icons';
import { useLayoutConfig } from '../contexts/LayoutContext';
import { addCourse, removeCourse, updateCourse } from '../features/courses/coursesSlice';
import { alert, getFileUrl } from '../hooks/function';
import { RootState } from '../types';

type EditorTab = 'settings' | 'detail' | 'curriculum' | 'completion';

// Sidebar width is tuned so the main form column stays close to a comfortable
// reading measure (~700-800px) on common laptop widths.
const SIDEBAR_WIDTH = 300;

/**
 * Re-derive the display thumbnail: server payloads (load and save responses)
 * carry only the blob path, but the UI renders a short-lived SAS-signed URL.
 * Applied wherever we adopt a fresh course from the server so
 * `course.thumbnailUrl` stays valid.
 */
async function withThumbnailUrl(course: Course): Promise<Course> {
  const thumbnailUrl = course.thumbnail ? await getFileUrl(course.thumbnail) : undefined;
  return { ...course, ...(thumbnailUrl ? { thumbnailUrl } : {}) } as Course;
}

export default function EditCoursePage() {
  const { slug } = useParams<{ slug?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { setLayoutConfig } = useLayoutConfig();
  const currentUser = useSelector((state: RootState) => state.profile.user);

  const isCreate = !slug;
  const typeParam = searchParams.get('type');
  const defaultCourseType: CourseType | undefined =
    typeParam === 'path'
      ? CourseType.LearningPath
      : typeParam === 'event'
        ? CourseType.External
        : undefined;

  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(!isCreate);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState<EditorTab>('settings');

  // Authoring gate — see `isContentAuthorRole` for the Super-Admin-only
  // target + why `AccountAdmin` is still admitted.
  const isAdmin = isContentAuthorRole(currentUser?.role);

  // Page chrome — back to /courses + hide the global search (the page has
  // no use for it and two search inputs side-by-side is confusing).
  useEffect(() => {
    setLayoutConfig({
      showBackButton: true,
      backButtonText: 'Back to Content',
      backButtonAction: () => navigate('/courses'),
      hideGlobalSearch: true,
    });
    return () => {
      setLayoutConfig({ showBackButton: false, hideGlobalSearch: false });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch the course when editing. Re-runs when the slug changes (e.g.
  // after a create → redirect into edit mode).
  useEffect(() => {
    if (isCreate) {
      setCourse(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const fetched = await coursesClient.getCourseBySlug(slug!);
        if (cancelled) return;
        const next = await withThumbnailUrl(fetched);
        if (cancelled) return;
        setCourse(next);
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, isCreate]);

  useEffect(() => {
    if (!course && activeTab !== 'settings') setActiveTab('settings');
  }, [course, activeTab]);

  const handleSave = async (payload: any): Promise<Course> => {
    if (course?.id) {
      const id = course.id;
      // Send the full merged payload so no server-bound field is dropped, but
      // adopt the server's enriched response as the new state rather than the
      // optimistic merge. The scalar settings block is full-replaced on save,
      // so a hand-merged copy would leave the flattened top-level fields
      // (authorId, courseCode, …) stale — and a later tab save, which rebuilds
      // settings from those fields, would then revert them on the server.
      const merged = { ...course, ...payload, id } as Course;
      const saved = await withThumbnailUrl(await coursesClient.updateCourse(id, merged as any));
      dispatch(updateCourse({ id, course: saved }));
      setCourse(saved);
      alert(enqueueSnackbar, 'Saved', 'success');
      return saved;
    }
    const created = await coursesClient.addCourse(payload);
    dispatch(addCourse({ ...payload, id: created.id }));
    alert(enqueueSnackbar, 'Course created', 'success');
    if (created.slug) {
      navigate(`/courses/${created.slug}/edit`, { replace: true });
    }
    return created;
  };

  // One-click publish / unpublish toggle from the sidebar status section.
  // Sends the loaded course with isDraft flipped (no settings key → the server
  // leaves the settings blob untouched), then adopts the server response so
  // state stays reconciled with the DB.
  const handleTogglePublish = async () => {
    if (!course?.id) return;
    const nextIsDraft = !course.isDraft;
    const merged = { ...course, isDraft: nextIsDraft } as Course;
    try {
      const saved = await withThumbnailUrl(await coursesClient.updateCourse(course.id, merged as any));
      dispatch(updateCourse({ id: course.id, course: saved }));
      setCourse(saved);
      alert(enqueueSnackbar, nextIsDraft ? 'Course unpublished' : 'Course published', 'success');
    } catch {
      alert(enqueueSnackbar, 'Failed to update status', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await coursesClient.deleteCourse(id);
      dispatch(removeCourse(id));
      alert(enqueueSnackbar, 'Course deleted', 'success');
      navigate('/courses');
    } catch {
      alert(enqueueSnackbar, 'Failed to delete course', 'error');
    }
  };

  const handleCancel = () => navigate('/courses');

  const refetchCourse = async () => {
    if (!slug) return;
    try {
      setCourse(await withThumbnailUrl(await coursesClient.getCourseBySlug(slug)));
    } catch {
      /* keep last good state */
    }
  };

  const previewAsLearner = () => {
    if (course?.slug) navigate(`/courses/${course.slug}`);
  };

  const copyValue = async (text: string, label: string) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      alert(enqueueSnackbar, `${label} copied`, 'success');
    } catch {
      alert(enqueueSnackbar, `Couldn't copy ${label.toLowerCase()}`, 'error');
    }
  };

  const copySlug = () => {
    if (course?.slug) copyValue(`/courses/${course.slug}`, 'URL');
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="text.secondary">You don't have permission to edit content.</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isCreate && notFound) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h5" color="text.secondary">
          Course not found
        </Typography>
      </Box>
    );
  }

  const pageTitle = isCreate
    ? defaultCourseType === CourseType.LearningPath
      ? 'Create Learning Path'
      : 'Create Course'
    : course?.title || 'Edit Course';

  const typeLabel =
    (course?.courseType ?? defaultCourseType) === CourseType.LearningPath
      ? 'Learning Path'
      : (course?.courseType ?? defaultCourseType) === CourseType.External
      ? 'Event'
      : 'Course';

  const lockedTabs = !course;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', bgcolor: 'white' }}>
      {/* ── Header ────────────────────────────────────────────────────── */}
      <Box sx={{ px: 4, pt: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {pageTitle}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <Chip
                label={typeLabel}
                size="small"
                sx={{
                  fontWeight: 600,
                  ...(typeLabel === 'Learning Path'
                    ? { bgcolor: '#f3e5f5', color: '#4a148c' }
                    : typeLabel === 'Event'
                    ? { bgcolor: '#e3f2fd', color: '#0d47a1' }
                    : {}),
                }}
              />
              {course && (
                course.isDraft ? (
                  <Chip
                    label="Draft"
                    size="small"
                    sx={{ fontWeight: 600, bgcolor: 'warning.softBg', color: 'warning.softText' }}
                  />
                ) : (
                  <Chip
                    label="● Published"
                    size="small"
                    sx={{ fontWeight: 600, bgcolor: 'success.softBg', color: 'success.dark' }}
                  />
                )
              )}
              {isCreate && (
                <Typography variant="body2" color="text.secondary">
                  Save the basics first to unlock the other tabs.
                </Typography>
              )}
            </Box>
          </Box>

          {course && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                startIcon={<VisibilityIcon />}
                onClick={previewAsLearner}
              >
                Preview as learner
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Tabs ─────────────────────────────────────────────────────── */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 4 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v: EditorTab) => setActiveTab(v)}
          aria-label="Course editor tabs"
        >
          <Tab value="settings" label="Settings" />
          <Tab value="detail" label="Detail Page" disabled={lockedTabs} />
          <Tab value="curriculum" label="Curriculum" disabled={lockedTabs} />
          <Tab value="completion" label="Completion" disabled={lockedTabs} />
        </Tabs>
      </Box>

      {/* ── Body: two-column on most tabs (metadata sidebar on the
          right). On Curriculum the page sidebar is hidden — that tab
          owns its own two-column layout with a lesson preview, which
          is much more useful while authoring. */}
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          px: 4,
          py: 3,
          alignItems: 'flex-start',
        }}
      >
        <Box
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            maxWidth:
              course && activeTab === 'settings'
                ? `calc(100% - ${SIDEBAR_WIDTH + 24}px)`
                : '100%',
          }}
        >
          {activeTab === 'settings' && (
            <EditCourseForm
              course={course}
              defaultCourseType={defaultCourseType}
              onSave={handleSave}
              onCancel={handleCancel}
              onDelete={handleDelete}
            />
          )}
          {activeTab === 'detail' && (
            <CourseDetailPageTab course={course} onCourseChanged={refetchCourse} />
          )}
          {activeTab === 'curriculum' && (
            <CourseCurriculumTab course={course} onCourseChanged={refetchCourse} />
          )}
          {activeTab === 'completion' && (
            <CourseCompletionTab course={course} onCourseChanged={refetchCourse} />
          )}
        </Box>

        {/* Metadata sidebar — only on the Settings tab. Detail Page,
            Curriculum, and Completion get the full content width so
            their two-column layouts (form + preview, builder + preview)
            aren't squeezed by the metadata column. */}
        {course && activeTab === 'settings' && (() => {
          const published = !course.isDraft;
          const code = course.courseCode ?? '';
          // Soft status palette — a tinted banner reads as "designed"
          // far more than a bare dot + label, and the colour carries the
          // published/draft meaning at a glance.
          const statusColors = published
            ? { bg: '#f1f8f3', border: '#cfe8d6', dot: '#1b5e20', text: '#1b5e20' }
            : { bg: '#fffaf0', border: '#f1e3c0', dot: '#c08a1e', text: '#8a6313' };

          // Shared bits so the metadata rows stay on one rhythm.
          const labelSx = {
            display: 'block',
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            fontWeight: 700,
            fontSize: '0.66rem',
            color: 'text.secondary',
          } as const;

          // Monospace "code chip" for machine values (Course ID, URL),
          // with an inline copy affordance.
          const CodeValue = ({ value, title, copyLabel }: { value: string; title?: string; copyLabel: string }) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: 'grey.100',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  color: 'text.primary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={title ?? value}
              >
                {value}
              </Box>
              <Tooltip title={`Copy ${copyLabel.toLowerCase()}`}>
                <IconButton
                  size="small"
                  onClick={() => copyValue(value, copyLabel)}
                  sx={{ flexShrink: 0, color: 'text.secondary' }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );

          return (
            <Card
              sx={{
                width: SIDEBAR_WIDTH,
                flexShrink: 0,
                position: 'sticky',
                top: 16,
                p: 0,
                overflow: 'hidden',
                borderRadius: 2,
              }}
            >
              {/* ── Status banner ──────────────────────────────────── */}
              <Box
                sx={{
                  p: 2.5,
                  bgcolor: statusColors.bg,
                  borderBottom: '1px solid',
                  borderColor: statusColors.border,
                }}
              >
                <Typography variant="caption" sx={labelSx}>
                  Status
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      bgcolor: statusColors.dot,
                      flexShrink: 0,
                      boxShadow: `0 0 0 3px ${statusColors.bg}, 0 0 0 4px ${statusColors.border}`,
                    }}
                  />
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: statusColors.text, lineHeight: 1.2 }}>
                    {published ? 'Published' : 'Draft'}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {published ? 'Visible in the catalog.' : 'Not visible to learners yet.'}
                </Typography>
                {/* Inline publish/unpublish — primary gradient when
                    drafting (push it live), quiet outlined when live. */}
                {course.isDraft ? (
                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    onClick={handleTogglePublish}
                    sx={{ mt: 1.75 }}
                  >
                    Publish
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    onClick={handleTogglePublish}
                    sx={{
                      mt: 1.75,
                      bgcolor: 'background.paper',
                      color: 'text.primary',
                      borderColor: 'rgba(0,0,0,0.23)',
                      '&:hover': { borderColor: 'rgba(0,0,0,0.5)', bgcolor: 'action.hover' },
                    }}
                  >
                    Unpublish
                  </Button>
                )}
              </Box>

              {/* ── Metadata list ──────────────────────────────────── */}
              <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ pb: 1.75 }}>
                  <Typography variant="caption" sx={labelSx}>
                    Type
                  </Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                    {typeLabel}
                  </Typography>
                </Box>

                {/* Admin-set course ID, if saved (`course.courseCode`). */}
                {code && (
                  <>
                    <Divider />
                    <Box sx={{ py: 1.75 }}>
                      <Typography variant="caption" sx={labelSx}>
                        Course ID
                      </Typography>
                      <CodeValue value={code} copyLabel="Course ID" />
                    </Box>
                  </>
                )}

                <Divider />
                <Box sx={{ py: 1.75 }}>
                  <Typography variant="caption" sx={labelSx}>
                    URL
                  </Typography>
                  <CodeValue value={`/courses/${course.slug}`} copyLabel="URL" />
                </Box>

                {course.updatedAt && (
                  <>
                    <Divider />
                    <Box sx={{ py: 1.75 }}>
                      <Typography variant="caption" sx={labelSx}>
                        Last Updated
                      </Typography>
                      <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                        {new Date(course.updatedAt).toLocaleString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </Typography>
                    </Box>
                  </>
                )}

                {course.createdAt && (
                  <>
                    <Divider />
                    <Box sx={{ pt: 1.75 }}>
                      <Typography variant="caption" sx={labelSx}>
                        Created
                      </Typography>
                      <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                        {new Date(course.createdAt).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            </Card>
          );
        })()}
      </Box>

    </Box>
  );
}
